import React, { useEffect, useState, useMemo, useRef } from "react";
import { priceForMargin, marginOnCost, markupFor, markupLabel, ruleFromSettings, DEFAULT_RULE } from "@/planner/lib/pricing";
import { api, fileUrl } from "@/planner/lib/apiClient";
import { productLabel } from "@/planner/lib/format";
import { useAuth } from "@/planner/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CATEGORIES, catColor, ITEM_NUMBER_STORES, dealSticker } from "@/planner/lib/vendy";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { toast } from "sonner";
import { Plus, Search, Pencil, Trash2, History, TrendingDown, Award, Flag, Eye, EyeOff, AlertTriangle, User, CheckCircle2, XCircle, ShieldAlert, ScrollText, Bot, Tag, DollarSign, Download, Upload, LayoutGrid, Rows3, Table2, Layers, ChevronDown, Check, GitMerge, ImageIcon, Copy, ChevronUp, GripVertical, Camera, RefreshCw } from "lucide-react";
import { photoToDataUrl, uploadProductPhoto } from "@/planner/lib/productPhoto";
import PhotoPicker from "@/planner/components/PhotoPicker";
import SandstarCloudDialog from "@/planner/components/SandstarCloudDialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Tooltip as UiTooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

// Hover label for a button. The span lets it show on disabled buttons too.
const Tip = ({ label, children }) => (
  <UiTooltip>
    <TooltipTrigger asChild><span className="inline-flex">{children}</span></TooltipTrigger>
    <TooltipContent>{label}</TooltipContent>
  </UiTooltip>
);
import { Checkbox } from "@/components/ui/checkbox";

const STORE_COLORS = ["#0E7490", "#D97706", "#2563EB", "#059669", "#E11D48", "#4F46E5"];

// Transform flat trend points into per-date rows with one column per store.
function buildChartData(trends) {
  const rows = {};
  for (const p of trends.points || []) {
    if (p.unit_price == null) continue;
    const d = p.date || "";
    if (!rows[d]) rows[d] = { date: d };
    // keep the lowest unit price seen per store/date
    if (rows[d][p.store] == null || p.unit_price < rows[d][p.store]) rows[d][p.store] = p.unit_price;
  }
  return Object.values(rows).sort((a, b) => (a.date < b.date ? -1 : 1));
}

const empty = { upc: "", name: "", category: "Soda", variant: "", size: "", target_cost: "", store_item_numbers: {}, cost: "", vend_price: "", case_pack: "", case_price: "", target_margin_pct: "", image_path: "" };

// vend price for a target margin %: markup on cost, rounded up to the next $0.25.
const vendForMargin = (cost, m) => priceForMargin(cost, m);
// live margin info for a saved product row, given the account's global margin.
const productMargin = (p, globalMargin) => {
  const uc = (p.case_price != null && p.case_pack) ? p.case_price / p.case_pack : (p.cost != null ? p.cost : null);
  const vend = p.vend_price != null ? p.vend_price : (uc != null ? vendForMargin(uc, p.target_margin_pct ?? globalMargin) : null);
  const target = markupFor(uc, p.target_margin_pct ?? globalMargin);
  const margin = vend && vend > 0 ? marginOnCost(uc, vend) : null;
  return { uc, vend, target, margin, below: margin != null && margin < target };
};

const SIZE_SUGGESTIONS = ["12oz can", "16.9oz bottle", "20oz bottle", "7.5oz mini can", "8.4oz can", "16oz can", "1oz", "1.5oz", "2.5oz", "2.75oz", "3oz", "3.5oz", "5oz"];

// Effective per-unit cost: prefer case price / case pack, else the manual cost.
const effUnitCost = (form) => {
  const cp = Number(form.case_pack);
  if (form.case_price !== "" && form.case_price != null && cp > 0) return Number(form.case_price) / cp;
  if (form.cost !== "" && form.cost != null) return Number(form.cost);
  return null;
};
const round225 = (c) => { const n = Number(c); return n > 0 ? (Math.round(n * 2.25 / 0.25) * 0.25).toFixed(2) : ""; };

// One collapsible entry that groups all sizes sharing the same product name.
// Each size can be edited inline (size, vend, cost, case price) and new sizes
// can be added without touching the underlying per-size product docs.
const blankSize = { size: "", vend_price: "", cost: "", case_price: "" };

const SizeGroup = ({ group, canEdit, globalMargin, onChanged, onHistory, onMerge, onDuplicate }) => {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [savingId, setSavingId] = useState(null);
  const [newRow, setNewRow] = useState(blankSize);
  const [adding, setAdding] = useState(false);
  const [newFlavor, setNewFlavor] = useState("");
  const [addingFlavor, setAddingFlavor] = useState(false);
  const c = catColor(group.category);

  useEffect(() => {
    const ordered = [...group.items].sort((a, b) => (a.sort_order ?? 1e9) - (b.sort_order ?? 1e9));
    setRows(ordered.map((p) => ({
      id: p.id, variant: p.variant || "", size: p.size || "", vend_price: p.vend_price ?? "",
      cost: p.cost ?? "", case_price: p.case_price ?? "", _orig: p,
    })));
  }, [group]);

  const upd = (id, field, val) => setRows((rs) => rs.map((r) => (r.id === id ? { ...r, [field]: val } : r)));
  const num = (v) => (v === "" || v === null ? null : Number(v));

  const saveRow = async (r) => {
    setSavingId(r.id);
    try {
      const p = r._orig;
      await api.put(`/products/${r.id}`, {
        upc: p.upc || "", name: p.name, category: p.category, variant: (r.variant || "").trim(),
        size: r.size.trim(), cost: num(r.cost), vend_price: num(r.vend_price),
        case_pack: p.case_pack ?? null, case_price: num(r.case_price),
        target_cost: p.target_cost ?? null, target_margin_pct: p.target_margin_pct ?? null,
        store_item_numbers: p.store_item_numbers || {}, image_path: p.image_path || null,
      });
      toast.success(`Updated ${p.name}${r.variant ? ` (${r.variant})` : ""}`);
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSavingId(null); }
  };

  const delRow = async (r) => {
    if (rows.length === 1) return toast.error("A product needs at least one entry — edit it instead");
    if (!window.confirm(`Delete ${group.name}${r.variant ? ` ${r.variant}` : ""} (${r.size || "this size"})?`)) return;
    try { await api.delete(`/products/${r.id}`); toast.success("Removed"); onChanged(); }
    catch (e) { toast.error("Delete failed"); }
  };

  const addSize = async () => {
    if (!newRow.size.trim()) return toast.error("Enter a size (e.g. 20oz bottle)");
    setAdding(true);
    try {
      const base = group.items[0] || {};
      await api.post("/products", {
        upc: "", name: group.name, category: group.category, variant: base.variant || "",
        size: newRow.size.trim(), cost: num(newRow.cost), vend_price: num(newRow.vend_price),
        case_pack: null, case_price: num(newRow.case_price),
        target_cost: null, target_margin_pct: null, store_item_numbers: {}, image_path: null,
      });
      toast.success(`Added ${group.name} (${newRow.size.trim()})`);
      setNewRow(blankSize); onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Add failed"); }
    finally { setAdding(false); }
  };

  // Add a new flavor across every existing size, copying that size's cost/prices.
  const addFlavor = async () => {
    const flavor = newFlavor.trim();
    if (!flavor) return toast.error("Enter a flavor name (e.g. Lemon-Lime)");
    setAddingFlavor(true);
    try {
      // one representative product per distinct size
      const bySize = new Map();
      for (const p of group.items) {
        const key = p.size || "";
        if (!bySize.has(key)) bySize.set(key, p);
      }
      // skip sizes where this flavor already exists
      const existing = new Set(group.items.filter((p) => (p.variant || "").toLowerCase() === flavor.toLowerCase()).map((p) => p.size || ""));
      let made = 0;
      for (const [size, rep] of bySize) {
        if (existing.has(size)) continue;
        await api.post("/products", {
          upc: "", name: group.name, category: group.category, variant: flavor,
          size, cost: rep.cost ?? null, vend_price: rep.vend_price ?? null,
          case_pack: rep.case_pack ?? null, case_price: rep.case_price ?? null,
          target_cost: rep.target_cost ?? null, target_margin_pct: rep.target_margin_pct ?? null,
          store_item_numbers: {}, image_path: null,
        });
        made += 1;
      }
      toast.success(made ? `Added ${flavor} in ${made} size${made !== 1 ? "s" : ""}` : `${flavor} already exists for all sizes`);
      setNewFlavor(""); onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Add flavor failed"); }
    finally { setAddingFlavor(false); }
  };

  // Move a row (by arrows or by dragging its handle) and save the new order for this product.
  const [dragIdx, setDragIdx] = useState(null);
  const move = (i, dir) => moveTo(i, i + dir);
  const moveTo = async (from, to) => {
    if (from === null || from === to || to < 0 || to >= rows.length) return;
    const next = [...rows];
    const [r] = next.splice(from, 1);
    next.splice(to, 0, r);
    setRows(next);
    try { await api.post("/products/reorder", { ids: next.map((r) => r.id) }); }
    catch (e) { toast.error("Could not save the new order"); setRows(rows); }
  };

  // Copy every flavor and size of this product under a new name (e.g. Coca-Cola -> Coca-Cola Zero Sugar).
  const duplicateGroup = async () => {
    const name = (window.prompt(`Copy all ${group.items.length} flavor/size rows of "${group.name}" to a new product named:`, `${group.name} (copy)`) || "").trim();
    if (!name) return;
    try {
      for (const p of group.items) {
        await api.post("/products", {
          upc: "", name, category: p.category, variant: p.variant || "", size: p.size || "",
          cost: p.cost ?? null, vend_price: p.vend_price ?? null, case_pack: p.case_pack ?? null, case_price: p.case_price ?? null,
          target_cost: p.target_cost ?? null, target_margin_pct: p.target_margin_pct ?? null, store_item_numbers: {}, image_path: p.image_path || null,
        });
      }
      toast.success(`Made "${name}" with ${group.items.length} row${group.items.length !== 1 ? "s" : ""}. Edit them there.`);
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Duplicate failed"); }
  };

  const dirty = (r) => {
    const p = r._orig;
    return r.size !== (p.size || "") || (r.variant || "") !== (p.variant || "") ||
      String(r.vend_price) !== String(p.vend_price ?? "") ||
      String(r.cost) !== String(p.cost ?? "") || String(r.case_price) !== String(p.case_price ?? "");
  };

  return (
    <Card data-testid={`product-group-${group.name}`} style={{ borderLeft: `6px solid ${c.stripe}` }}>
      <Collapsible open={open} onOpenChange={setOpen}>
        <div className="flex items-center gap-1 pr-3">
          <CollapsibleTrigger asChild>
            <button type="button" className="flex flex-1 items-center justify-between gap-3 p-4 text-left" data-testid={`product-group-toggle-${group.name}`}>
              <div className="flex items-center gap-2">
                <span className="font-semibold" data-testid={`product-group-name-${group.name}`}>{group.name}</span>
                <Badge style={{ background: c.bg, color: c.text }}>{group.category}</Badge>
                <span className="text-xs text-muted-foreground">{group.items.length} size{group.items.length !== 1 ? "s" : ""}</span>
              </div>
              <ChevronDown className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`} />
            </button>
          </CollapsibleTrigger>
          {canEdit && (
            <Tip label="Copy this product, with all its flavors and sizes, under a new name">
              <Button variant="ghost" size="sm" className="shrink-0 text-muted-foreground" data-testid={`product-group-duplicate-${group.name}`} onClick={duplicateGroup}><Copy className="mr-1 h-3.5 w-3.5" />Duplicate…</Button>
            </Tip>
          )}
          {canEdit && onMerge && (
            <Tip label="Move these rows into another product (for duplicates with different names)">
              <Button variant="ghost" size="sm" className="shrink-0 text-muted-foreground" data-testid={`product-group-merge-${group.name}`} onClick={() => onMerge(group)}><GitMerge className="mr-1 h-3.5 w-3.5" />Merge…</Button>
            </Tip>
          )}
        </div>
        <CollapsibleContent>
          <div className="space-y-2 border-t p-3">
            {rows.map((r, i) => {
              const mi = productMargin({ ...r._orig, cost: num(r.cost), vend_price: num(r.vend_price) }, globalMargin);
              return (
                <div key={r.id} className={`flex flex-wrap items-end gap-2 rounded-lg bg-muted/40 p-2 ${dragIdx === i ? "opacity-50" : ""}`} data-testid={`group-size-row-${r.id}`}
                  onDragOver={(e) => { if (dragIdx !== null) e.preventDefault(); }}
                  onDrop={() => { moveTo(dragIdx, i); setDragIdx(null); }}>
                  {canEdit && rows.length > 1 && (
                    <div className="flex items-center">
                    <Tip label="Drag to reorder">
                      <span draggable onDragStart={() => setDragIdx(i)} onDragEnd={() => setDragIdx(null)}
                        className="cursor-grab px-0.5 text-muted-foreground active:cursor-grabbing" data-testid={`group-grip-${r.id}`}><GripVertical className="h-4 w-4" /></span>
                    </Tip>
                    <div className="flex flex-col">
                      <Tip label="Move up"><Button variant="ghost" size="icon" className="h-5 w-6" aria-label="Move up" data-testid={`group-up-${r.id}`} disabled={i === 0} onClick={() => move(i, -1)}><ChevronUp className="h-3.5 w-3.5" /></Button></Tip>
                      <Tip label="Move down"><Button variant="ghost" size="icon" className="h-5 w-6" aria-label="Move down" data-testid={`group-down-${r.id}`} disabled={i === rows.length - 1} onClick={() => move(i, 1)}><ChevronDown className="h-3.5 w-3.5" /></Button></Tip>
                    </div>
                    </div>
                  )}
                  <div className="w-28"><Label className="text-[11px]">Flavor</Label><Input data-testid={`group-flavor-input-${r.id}`} value={r.variant} disabled={!canEdit} onChange={(e) => upd(r.id, "variant", e.target.value)} placeholder="—" /></div>
                  <div className="min-w-[120px] flex-1"><Label className="text-[11px]">Size</Label><Input data-testid={`group-size-input-${r.id}`} value={r.size} disabled={!canEdit} onChange={(e) => upd(r.id, "size", e.target.value)} placeholder="Add a size, e.g. 20oz bottle" /></div>
                  <div className="w-24"><Label className="text-[11px]">Vend $</Label><Input data-testid={`group-vend-input-${r.id}`} type="number" step="0.01" value={r.vend_price} disabled={!canEdit} onChange={(e) => upd(r.id, "vend_price", e.target.value)} /></div>
                  <div className="w-24"><Label className="text-[11px]">Cost $</Label><Input data-testid={`group-cost-input-${r.id}`} type="number" step="0.01" value={r.cost} disabled={!canEdit} onChange={(e) => upd(r.id, "cost", e.target.value)} /></div>
                  <div className="w-24"><Label className="text-[11px]">Case $</Label><Input data-testid={`group-case-input-${r.id}`} type="number" step="0.01" value={r.case_price} disabled={!canEdit} onChange={(e) => upd(r.id, "case_price", e.target.value)} /></div>
                  <div className="flex w-16 flex-col">
                    <span className="text-[11px] text-muted-foreground">Margin</span>
                    <span className={`text-sm font-semibold ${mi.margin == null ? "text-muted-foreground" : mi.below ? "text-destructive" : "text-green-700"}`}>{mi.margin != null ? `${mi.margin}%` : "—"}</span>
                  </div>
                  {canEdit && (
                    <div className="flex gap-1">
                      <Tip label={dirty(r) ? "Save the changes in this row" : "Nothing to save yet. Edit a field first."}>
                        <Button size="sm" data-testid={`group-save-${r.id}`} onClick={() => saveRow(r)} disabled={savingId === r.id || !dirty(r)}><Check className="mr-1 h-3.5 w-3.5" />Save</Button>
                      </Tip>
                      <Tip label="Duplicate this row, then change its flavor, size or prices">
                        <Button variant="ghost" size="icon" aria-label="Duplicate" data-testid={`group-duplicate-${r.id}`} onClick={() => onDuplicate(r._orig)}><Copy className="h-4 w-4" /></Button>
                      </Tip>
                      <Tip label="Price history and store prices">
                        <Button variant="ghost" size="icon" aria-label="Price history" data-testid={`group-history-${r.id}`} onClick={() => onHistory(r.id)}><History className="h-4 w-4" /></Button>
                      </Tip>
                      <Tip label="Delete this row">
                        <Button variant="ghost" size="icon" aria-label="Delete" data-testid={`group-delete-${r.id}`} onClick={() => delRow(r)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                      </Tip>
                    </div>
                  )}
                </div>
              );
            })}
            {canEdit && (
              <div className="flex flex-wrap items-end gap-2 rounded-lg border border-dashed p-2" data-testid={`group-add-row-${group.name}`}>
                <div className="min-w-[130px] flex-1"><Label className="text-[11px]">New size</Label><Input data-testid={`group-add-size-${group.name}`} value={newRow.size} onChange={(e) => setNewRow({ ...newRow, size: e.target.value })} placeholder="e.g. 2L bottle" /></div>
                <div className="w-24"><Label className="text-[11px]">Vend $</Label><Input data-testid={`group-add-vend-${group.name}`} type="number" step="0.01" value={newRow.vend_price} onChange={(e) => setNewRow({ ...newRow, vend_price: e.target.value })} /></div>
                <div className="w-24"><Label className="text-[11px]">Cost $</Label><Input type="number" step="0.01" value={newRow.cost} onChange={(e) => setNewRow({ ...newRow, cost: e.target.value })} /></div>
                <div className="w-24"><Label className="text-[11px]">Case $</Label><Input type="number" step="0.01" value={newRow.case_price} onChange={(e) => setNewRow({ ...newRow, case_price: e.target.value })} /></div>
                <Tip label="Add another size of this product"><Button variant="outline" size="sm" data-testid={`group-add-size-btn-${group.name}`} onClick={addSize} disabled={adding}><Plus className="mr-1 h-3.5 w-3.5" />Add size</Button></Tip>
              </div>
            )}
            {canEdit && (
              <div className="flex flex-wrap items-end gap-2 rounded-lg border border-dashed border-primary/40 bg-primary/5 p-2" data-testid={`group-add-flavor-row-${group.name}`}>
                <div className="min-w-[160px] flex-1"><Label className="text-[11px]">New flavor (added to every size)</Label><Input data-testid={`group-add-flavor-${group.name}`} value={newFlavor} onChange={(e) => setNewFlavor(e.target.value)} placeholder="e.g. Lemon-Lime" /></div>
                <Tip label="Add this flavor in every size, copying each size's prices"><Button variant="outline" size="sm" data-testid={`group-add-flavor-btn-${group.name}`} onClick={addFlavor} disabled={addingFlavor}><Tag className="mr-1 h-3.5 w-3.5" />Add flavor</Button></Tip>
              </div>
            )}
          </div>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
};


export default function Products() {
  const { canEdit } = useAuth();
  // Price-capture review (pending/flagged store prices) isn't part of the website port; store
  // prices live in Stock Sourcing instead.
  const isAdmin = false;
  const [items, setItems] = useState([]);
  // /admin/planner/products?q=... (from workspace search) opens with the search filled in.
  const [search, setSearch] = useState(() => new URLSearchParams(window.location.search).get("q") || "");
  const [cat, setCat] = useState("all");
  const [sizeFilter, setSizeFilter] = useState("all");
  const [globalMargin, setGlobalMargin] = useState(DEFAULT_RULE);
  const [cloudOpen, setCloudOpen] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [duplicating, setDuplicating] = useState(false);
  const [detail, setDetail] = useState(null);
  const [trends, setTrends] = useState(null);
  const [pending, setPending] = useState([]);
  const [logOpen, setLogOpen] = useState(false);
  const [log, setLog] = useState(null);
  const [repriceOpen, setRepriceOpen] = useState(false);
  const [repriceBusy, setRepriceBusy] = useState(false);
  const [repriceMargin, setRepriceMargin] = useState("rule");
  const [saveDefaultMargin, setSaveDefaultMargin] = useState(false);
  const [importing, setImporting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const importRef = useRef(null);
  const [view, setView] = useState(() => localStorage.getItem("productsView") || "grid");
  const changeView = (v) => { if (v) { setView(v); localStorage.setItem("productsView", v); } };
  const [groupSizes, setGroupSizes] = useState(() => localStorage.getItem("productsGroupSizes") !== "0");
  const toggleGroupSizes = () => setGroupSizes((v) => { const nv = !v; localStorage.setItem("productsGroupSizes", nv ? "1" : "0"); return nv; });
  const [mergeSrc, setMergeSrc] = useState(null); // group being merged into another
  const [mergeTarget, setMergeTarget] = useState("");
  const [mergeBusy, setMergeBusy] = useState(false);
  const doMerge = async () => {
    if (!mergeSrc || !mergeTarget) return;
    setMergeBusy(true);
    try {
      for (const p of mergeSrc.items) {
        await api.put(`/products/${p.id}`, {
          upc: p.upc || "", name: mergeTarget, category: p.category, variant: p.variant || "",
          size: p.size || "", cost: p.cost ?? null, vend_price: p.vend_price ?? null,
          case_pack: p.case_pack ?? null, case_price: p.case_price ?? null,
          target_cost: p.target_cost ?? null, target_margin_pct: p.target_margin_pct ?? null,
          store_item_numbers: p.store_item_numbers || {}, image_path: p.image_path || null,
        });
      }
      toast.success(`Merged "${mergeSrc.name}" into "${mergeTarget}"`);
      setMergeSrc(null); setMergeTarget("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Merge failed"); }
    finally { setMergeBusy(false); }
  };
  // Open the product form pre-filled from p as a new product (no barcode), to change its size,
  // flavor or prices before saving.
  const startDuplicate = (p) => {
    startEdit(p);
    setEditId(null);
    setForm((f) => ({ ...f, id: undefined, upc: "", favorite: undefined }));
    setDuplicating(true);
  };
  const startEdit = (p) => {
    setForm({ ...p, target_cost: p.target_cost ?? "", store_item_numbers: p.store_item_numbers || {}, cost: p.cost ?? "", vend_price: p.vend_price ?? "", case_pack: p.case_pack ?? "", case_price: p.case_price ?? "", target_margin_pct: p.target_margin_pct ?? "", image_path: p.image_path || "" });
    setEditId(p.id);
    setOpen(true);
  };

  // Memoised so the trend chart data is only recomputed when the trends change.
  const chartData = useMemo(() => (trends ? buildChartData(trends) : []), [trends]);
  const sizeOptions = useMemo(() => Array.from(new Set(items.map((p) => p.size).filter(Boolean))).sort(), [items]);
  const visibleItems = useMemo(() => (sizeFilter === "all" ? items : items.filter((p) => p.size === sizeFilter)), [items, sizeFilter]);

  // Group products that share the same name (case-insensitive) so all sizes of
  // e.g. "Coca-Cola Classic" collapse into one expandable entry.
  const sizeGroups = useMemo(() => {
    const m = new Map();
    for (const p of visibleItems) {
      const key = (p.name || "").trim().toLowerCase();
      if (!m.has(key)) m.set(key, { name: p.name, category: p.category, items: [] });
      m.get(key).items.push(p);
    }
    return Array.from(m.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [visibleItems]);

  // Products whose recommended vend price (to hit the chosen target margin)
  // differs from what's currently set — candidates for one-tap bulk reprice.
  const repriceCandidates = useMemo(() => {
    const byRule = repriceMargin === "rule" || !Number(repriceMargin);
    return items.map((p) => {
      const mi = productMargin(p, globalMargin);
      const m = byRule ? (p.target_margin_pct ?? globalMargin) : Number(repriceMargin);
      const rec = mi.uc != null ? vendForMargin(mi.uc, m) : 0;
      const current = p.vend_price != null ? Number(p.vend_price) : null;
      return { p, uc: mi.uc, current, rec, target: m, byRule };
    // The markup rule only ever raises prices: a price Dan set above it (e.g. Red Bull at $4.50) stays.
    }).filter((x) => x.rec > 0 && x.current !== x.rec && !(x.byRule && x.current != null && x.current > x.rec));
  }, [items, globalMargin, repriceMargin]);
  const imgUrl = (path) => fileUrl(path);

  const loadPending = async () => {
    if (!isAdmin) return;
    try { const { data } = await api.get("/captures/pending"); setPending(data); }
    catch (err) { if (err?.response?.status !== 403) console.error("Failed to load pending reviews:", err); }
  };

  const openLog = async () => {
    setLog(null); setLogOpen(true);
    try { const { data } = await api.get("/captures/review-log"); setLog(data); }
    catch (e) { setLog([]); toast.error("Could not load review history"); }
  };

  const load = async () => {
    const params = {};
    if (search) params.search = search;
    if (cat !== "all") params.category = cat;
    const { data } = await api.get("/products", { params });
    setItems(data);
  };

  const exportCsv = async () => {
    try {
      const res = await api.get("/products/export", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `products-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Exported catalog to CSV");
    } catch (e) { toast.error("Export failed"); }
  };

  const importCsv = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting the same file
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/products/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const parts = [];
      if (data.created) parts.push(`${data.created} added`);
      if (data.updated) parts.push(`${data.updated} updated`);
      if (data.skipped) parts.push(`${data.skipped} skipped`);
      if (data.photos) parts.push(`${data.photos} photo${data.photos === 1 ? "" : "s"} added`);
      toast.success(`Import complete — ${parts.join(", ") || "no changes"}`);
      if (data.errors?.length) toast.error(`${data.errors.length} row error(s): ${data.errors[0]}`);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Import failed — check the CSV format"); }
    finally { setImporting(false); }
  };
  useEffect(() => { load(); }, [search, cat]);
  useEffect(() => { api.get("/settings").then((r) => setGlobalMargin(ruleFromSettings(r.data))).catch(() => {}); }, []);
  useEffect(() => { loadPending(); }, [isAdmin]);

  // Your own photo: uploaded right away for a saved product, or after saving a new one.
  const productPhotoInput = useRef(null);
  const [pendingPhoto, setPendingPhoto] = useState(null);
  const [photoBusy, setPhotoBusy] = useState(false);
  const pickProductPhoto = async (file) => {
    if (!file) return;
    setPhotoBusy(true);
    try {
      const dataUrl = await photoToDataUrl(file);
      if (editId) {
        const p = await uploadProductPhoto(editId, dataUrl);
        setForm((f) => ({ ...f, image_path: p.image_path })); setPendingPhoto(null);
        toast.success("Photo saved");
      } else setPendingPhoto(dataUrl);
    } catch (e) { toast.error(e?.response?.data?.detail || e.message || "Couldn't use that photo"); }
    finally { setPhotoBusy(false); }
  };

  const save = async () => {
    if (!form.name) return toast.error("Name is required");
    const payload = { ...form, target_cost: form.target_cost === "" ? null : Number(form.target_cost), store_item_numbers: form.store_item_numbers || {}, cost: form.cost === "" ? null : Number(form.cost), vend_price: form.vend_price === "" ? null : Number(form.vend_price), case_pack: form.case_pack === "" ? null : Number(form.case_pack), case_price: form.case_price === "" ? null : Number(form.case_price), target_margin_pct: form.target_margin_pct === "" ? null : Number(form.target_margin_pct), image_path: form.image_path || null };
    try {
      if (editId) await api.put(`/products/${editId}`, payload);
      else {
        const { data } = await api.post("/products", payload);
        if (pendingPhoto) await uploadProductPhoto(data.id, pendingPhoto).catch(() => toast.error("Product added, but the photo didn't save. Add it again."));
      }
      setPendingPhoto(null);
      toast.success(editId ? "Product updated" : duplicating ? "Copy added" : "Product added");
      setOpen(false); setForm(empty); setEditId(null); setDuplicating(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to save"); }
  };

  const openDetail = async (id) => {
    setTrends(null);
    const [{ data }, t] = await Promise.all([
      api.get(`/products/${id}`),
      api.get(`/products/${id}/trends`).catch(() => ({ data: null })),
    ]);
    setDetail(data);
    setTrends(t.data);
  };

  const del = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    await api.delete(`/products/${id}`); toast.success("Deleted"); load();
  };

  const flagCapture = async (captureId, patch) => {
    try { await api.put(`/captures/${captureId}/flag`, patch); if (detail) openDetail(detail.id); }
    catch (e) { toast.error("Action failed"); }
  };

  const reviewCapture = async (captureId, action) => {
    try {
      await api.put(`/captures/${captureId}/review`, { action });
      toast.success(action === "approve" ? "Approved — now counts toward the average" : "Rejected — kept hidden");
      loadPending();
      if (detail) openDetail(detail.id);
    } catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
  };

  const reviewAll = async (action) => {
    if (!window.confirm(`${action === "approve" ? "Approve" : "Reject"} all ${pending.length} pending outlier(s)?`)) return;
    try {
      const { data } = await api.put("/captures/review-all", { action });
      toast.success(`${action === "approve" ? "Approved" : "Rejected"} ${data.updated} entr${data.updated === 1 ? "y" : "ies"}`);
      loadPending();
      if (detail) openDetail(detail.id);
    } catch (e) { toast.error(e?.response?.data?.detail || "Bulk action failed"); }
  };

  const applyBulkReprice = async () => {
    if (repriceCandidates.length === 0) return;
    setRepriceBusy(true);
    try {
      const updates = repriceCandidates.map((x) => ({ id: x.p.id, vend_price: x.rec }));
      const { data } = await api.post("/products/bulk-reprice", { updates });
      const byRule = repriceMargin === "rule" || !Number(repriceMargin);
      let msg = `Repriced ${data.updated} product${data.updated === 1 ? "" : "s"} to ${byRule ? markupLabel(globalMargin) : `${Number(repriceMargin)}%`} markup`;
      if (saveDefaultMargin && !byRule) {
        try { const r = await api.put("/settings", { target_margin_pct: Number(repriceMargin), markup_tier_cost: 0 }); setGlobalMargin(ruleFromSettings(r.data)); msg += ` · saved as default`; }
        catch (_e) { /* non-blocking */ }
      }
      toast.success(msg);
      setRepriceOpen(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Bulk reprice failed"); }
    finally { setRepriceBusy(false); }
  };

  // Signs in to the Sandstar Ops account (login saved on the backend) and pulls the product library:
  // new products are added, blanks and missing photos filled; nothing set here is overwritten.
  const syncSandstar = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/sandstar/sync");
      const parts = [`${data.found} in Sandstar`];
      if (data.created) parts.push(`${data.created} added`);
      if (data.updated) parts.push(`${data.updated} updated`);
      if (data.photos) parts.push(`${data.photos} photo${data.photos === 1 ? "" : "s"}`);
      toast.success(`Sandstar sync${data.account ? ` (${data.account})` : ""}: ${parts.join(", ")}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Sandstar sync failed"); }
    finally { setSyncing(false); }
  };
  const missingPhotos = items.filter((p) => !fileUrl(p.image_path));
  const [photoQueue, setPhotoQueue] = useState(null);
  const [byUpcBusy, setByUpcBusy] = useState(false);
  // Barcode matches are exact, so do those automatically, then walk through the rest by name.
  const findPhotos = async () => {
    setByUpcBusy(true);
    let fresh = items;
    try {
      const { data } = await api.post("/products/photos/by-upc");
      if (data.found) { toast.success(`Found ${data.found} photo${data.found === 1 ? "" : "s"} by barcode`); fresh = (await api.get("/products")).data; setItems(fresh); }
    } catch (e) { toast.error(e?.response?.data?.detail || "Barcode photo lookup failed"); }
    finally { setByUpcBusy(false); }
    const rest = fresh.filter((p) => !fileUrl(p.image_path));
    if (rest.length) setPhotoQueue(rest);
  };

  return (
    <TooltipProvider delayDuration={150}>
    <div>
      <PhotoPicker open={!!photoQueue} queue={photoQueue} onClose={() => setPhotoQueue(null)}
        onSaved={(d) => setItems((list) => list.map((x) => (x.id === d.id ? { ...x, ...d } : x)))} />
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div>
          <h1 className="font-heading text-2xl font-bold">Products</h1>
          <p className="text-sm text-muted-foreground">UPC-based catalog. Variants are separate products.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {canEdit && missingPhotos.length > 0 && <Button variant="outline" data-testid="products-photos-button" onClick={findPhotos} disabled={byUpcBusy}><ImageIcon className="mr-2 h-4 w-4" />{byUpcBusy ? "Checking barcodes…" : `Add photos (${missingPhotos.length})`}</Button>}
          {isAdmin && <Button variant="outline" data-testid="review-history-button" onClick={openLog}><ScrollText className="mr-2 h-4 w-4" />Review history</Button>}
          {canEdit && <Button variant="outline" data-testid="products-sandstar-sync" onClick={syncSandstar} disabled={syncing} title="Pull products and photos from your Sandstar account"><RefreshCw className={`mr-2 h-4 w-4 ${syncing ? "animate-spin" : ""}`} />{syncing ? "Syncing…" : "Sync from Sandstar"}</Button>}
          {canEdit && <Button variant="outline" data-testid="products-sandstar-cloud" onClick={() => setCloudOpen(true)} title="Search Sandstar's cloud library for items to add"><Search className="mr-2 h-4 w-4" />Sandstar cloud library</Button>}
          <SandstarCloudDialog open={cloudOpen} onOpenChange={setCloudOpen} canEdit={canEdit} onAdded={load} />
          <Button variant="outline" data-testid="products-export-button" onClick={exportCsv}><Download className="mr-2 h-4 w-4" />Export CSV</Button>
          {canEdit && (
            <>
              <input ref={importRef} type="file" accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" className="hidden" data-testid="products-import-input" onChange={importCsv} />
              <Button variant="outline" data-testid="products-import-button" onClick={() => importRef.current?.click()} disabled={importing}><Upload className="mr-2 h-4 w-4" />{importing ? "Importing…" : "Import CSV/Excel"}</Button>
            </>
          )}
          {canEdit && (
            <Button variant="outline" data-testid="bulk-reprice-button" onClick={() => setRepriceOpen(true)}>
              <DollarSign className="mr-2 h-4 w-4" />Bulk price tune
              {repriceCandidates.length > 0 && <Badge className="ml-2" data-testid="bulk-reprice-count">{repriceCandidates.length}</Badge>}
            </Button>
          )}
          {canEdit && <Button data-testid="products-add-button" onClick={() => { setForm(empty); setEditId(null); setOpen(true); }}><Plus className="mr-2 h-4 w-4" />Add product</Button>}
        </div>
      </div>

      {isAdmin && pending.length > 0 && (
        <Card className="mb-5 border-amber-300 bg-amber-50/60 p-4" data-testid="pending-review-panel">
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <ShieldAlert className="h-4 w-4 text-amber-700" />
            <h2 className="font-heading font-semibold text-amber-900">Pending review</h2>
            <Badge className="bg-amber-500 text-white" data-testid="pending-review-count">{pending.length}</Badge>
            <span className="text-xs text-amber-800">Auto-hidden extreme outliers — approve to include in averages, or reject to keep hidden.</span>
            <div className="ml-auto flex items-center gap-2">
              <Button size="sm" variant="outline" className="border-green-300 text-green-700 hover:bg-green-50" data-testid="pending-approve-all" onClick={() => reviewAll("approve")}><CheckCircle2 className="mr-1 h-3.5 w-3.5" />Approve all</Button>
              <Button size="sm" variant="outline" className="border-red-300 text-red-700 hover:bg-red-50" data-testid="pending-reject-all" onClick={() => reviewAll("reject")}><XCircle className="mr-1 h-3.5 w-3.5" />Reject all</Button>
            </div>
          </div>
          <div className="space-y-2">
            {pending.map((h) => (
              <div key={h.id} className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-amber-200 bg-card p-3 text-sm text-card-foreground" data-testid={`pending-row-${h.id}`}>
                <div className="min-w-0">
                  <div className="font-medium">{h.product_name || h.product_title}</div>
                  <div className="text-xs text-muted-foreground flex items-center gap-1"><User className="h-3 w-3" />{h.user_name || "Unknown"} · {h.store_name} · {new Date(h.captured_date).toLocaleDateString()}</div>
                  {h.flag_reason && <div className="text-[11px] text-amber-700">{h.flag_reason}</div>}
                </div>
                <div className="flex items-center gap-2">
                  <div className="text-right"><div className="font-mono font-semibold">${h.captured_price}</div><div className="text-xs text-muted-foreground">${h.unit_price}/unit</div></div>
                  <Button size="sm" variant="outline" className="border-green-300 text-green-700 hover:bg-green-50" data-testid={`pending-approve-${h.id}`} onClick={() => reviewCapture(h.id, "approve")}><CheckCircle2 className="mr-1 h-3.5 w-3.5" />Approve</Button>
                  <Button size="sm" variant="outline" className="border-red-300 text-red-700 hover:bg-red-50" data-testid={`pending-reject-${h.id}`} onClick={() => reviewCapture(h.id, "reject")}><XCircle className="mr-1 h-3.5 w-3.5" />Reject</Button>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      <div className="flex flex-wrap gap-3 mb-4">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input data-testid="products-search-input" placeholder="Search name or UPC" className="pl-9" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <Select value={cat} onValueChange={setCat}>
          <SelectTrigger className="w-[160px]" data-testid="products-category-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All categories</SelectItem>
            {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={sizeFilter} onValueChange={setSizeFilter}>
          <SelectTrigger className="w-[150px]" data-testid="products-size-filter"><SelectValue placeholder="All sizes" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All sizes</SelectItem>
            {sizeOptions.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button variant={groupSizes ? "default" : "outline"} data-testid="products-group-sizes-toggle" onClick={toggleGroupSizes} className="ml-auto"><Layers className="mr-2 h-4 w-4" />Group sizes</Button>
        <ToggleGroup type="single" value={view} onValueChange={changeView} data-testid="products-view-toggle" disabled={groupSizes}>
          <ToggleGroupItem title={groupSizes ? "Grid view (turn off grouping by product first)" : "Grid view"} value="grid" aria-label="Grid view" data-testid="products-view-grid" disabled={groupSizes}><LayoutGrid className="h-4 w-4" /></ToggleGroupItem>
          <ToggleGroupItem title={groupSizes ? "List view (turn off grouping by product first)" : "List view"} value="list" aria-label="List view" data-testid="products-view-list" disabled={groupSizes}><Rows3 className="h-4 w-4" /></ToggleGroupItem>
          <ToggleGroupItem title={groupSizes ? "Table view (turn off grouping by product first)" : "Table view"} value="table" aria-label="Table view" data-testid="products-view-table" disabled={groupSizes}><Table2 className="h-4 w-4" /></ToggleGroupItem>
        </ToggleGroup>
      </div>

      {visibleItems.length === 0 ? (
        <Card className="p-10 text-center text-muted-foreground">No products match. Add your first product or adjust filters.</Card>
      ) : (
        <>
          {groupSizes && (
            <div className="flex flex-col gap-2" data-testid="products-grouped">
              {sizeGroups.map((g) => (
                <SizeGroup key={g.name} group={g} canEdit={canEdit} globalMargin={globalMargin} onChanged={load} onHistory={openDetail} onDuplicate={startDuplicate} onMerge={(grp) => { setMergeSrc(grp); setMergeTarget(""); }} />
              ))}
            </div>
          )}

          {!groupSizes && view === "grid" && (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {visibleItems.map((p) => {
                const c = catColor(p.category);
                return (
                  <Card key={p.id} className="p-4" data-testid={`product-card-${p.id}`} style={{ borderLeft: `6px solid ${c.stripe}` }}>
                    <div className="flex items-start justify-between">
                      <div className="min-w-0">
                        <div className="font-semibold leading-tight" data-testid={`product-name-${p.id}`}>{productLabel(p)}</div>
                        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                          {p.size ? <span className="rounded bg-muted px-1.5 py-0.5 font-medium text-foreground" data-testid={`product-size-${p.id}`}>{p.size}</span> : <span>—</span>}
                        </div>
                      </div>
                      <Badge style={{ background: c.bg, color: c.text }}>{p.category}</Badge>
                    </div>
                    {(() => { const mi = productMargin(p, globalMargin); return mi.margin != null ? (
                      <div className="mt-2 flex items-center gap-2 text-xs" data-testid={`product-margin-${p.id}`}>
                        <span className="text-muted-foreground">Margin</span>
                        <span className={`font-semibold ${mi.below ? "text-destructive" : "text-green-700"}`}>{mi.margin}%</span>
                        <span className="text-muted-foreground">/ target {mi.target}%</span>
                        {mi.below && <span className="inline-flex items-center gap-1 rounded-full bg-destructive/10 px-1.5 py-0.5 text-[10px] font-semibold text-destructive" data-testid={`product-lowmargin-${p.id}`}><AlertTriangle className="h-3 w-3" />Low margin</span>}
                      </div>
                    ) : null; })()}
                    <div className="mt-3 flex items-center justify-between text-xs text-muted-foreground">
                      <span className="font-mono" data-testid={`product-upc-${p.id}`}>{p.upc || "no UPC"}</span>
                      <span>{p.on_hand > 0 ? <span className="font-semibold text-green-700" data-testid={`product-onhand-${p.id}`}>{p.on_hand} on hand</span> : p.target_cost ? `target $${p.target_cost}` : ""}</span>
                    </div>
                    <div className="mt-3 flex gap-2">
                      <Button variant="outline" size="sm" data-testid={`product-history-${p.id}`} onClick={() => openDetail(p.id)}><History className="mr-1 h-3.5 w-3.5" />History</Button>
                      {canEdit && <>
                        <Tip label="Edit this product"><Button variant="ghost" size="icon" aria-label="Edit" data-testid={`product-edit-${p.id}`} onClick={() => startEdit(p)}><Pencil className="h-4 w-4" /></Button></Tip>
                        <Tip label="Duplicate, then change its flavor, size or prices"><Button variant="ghost" size="icon" aria-label="Duplicate" data-testid={`product-duplicate-${p.id}`} onClick={() => startDuplicate(p)}><Copy className="h-4 w-4" /></Button></Tip>
                        <Tip label="Delete this product"><Button variant="ghost" size="icon" aria-label="Delete" data-testid={`product-delete-${p.id}`} onClick={() => del(p.id)}><Trash2 className="h-4 w-4 text-destructive" /></Button></Tip>
                      </>}
                    </div>
                  </Card>
                );
              })}
            </div>
          )}

          {!groupSizes && view === "list" && (
            <div className="flex flex-col gap-2">
              {visibleItems.map((p) => {
                const c = catColor(p.category);
                const mi = productMargin(p, globalMargin);
                return (
                  <Card key={p.id} data-testid={`product-card-${p.id}`} className="flex items-center gap-3 p-3" style={{ borderLeft: `6px solid ${c.stripe}` }}>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="truncate font-semibold" data-testid={`product-name-${p.id}`}>{productLabel(p)}</span>
                        <Badge style={{ background: c.bg, color: c.text }}>{p.category}</Badge>
                        {p.size && <span className="rounded bg-muted px-1.5 py-0.5 text-xs font-medium" data-testid={`product-size-${p.id}`}>{p.size}</span>}
                      </div>
                      <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
                        <span className="font-mono" data-testid={`product-upc-${p.id}`}>{p.upc || "no UPC"}</span>
                        {mi.margin != null && <span data-testid={`product-margin-${p.id}`} className={mi.below ? "font-semibold text-destructive" : "font-semibold text-green-700"}>{mi.margin}% margin</span>}
                        {mi.below && <span className="inline-flex items-center gap-1 text-destructive" data-testid={`product-lowmargin-${p.id}`}><AlertTriangle className="h-3 w-3" />Low margin</span>}
                        {p.on_hand > 0 && <span className="font-semibold text-green-700" data-testid={`product-onhand-${p.id}`}>{p.on_hand} on hand</span>}
                      </div>
                    </div>
                    <div className="hidden flex-col items-end text-xs text-muted-foreground sm:flex">
                      {p.vend_price != null && <span className="font-mono text-sm text-foreground">${Number(p.vend_price).toFixed(2)}</span>}
                      {p.cost != null && <span>cost ${Number(p.cost).toFixed(2)}</span>}
                    </div>
                    <div className="flex shrink-0 gap-1">
                      <Button variant="outline" size="sm" data-testid={`product-history-${p.id}`} onClick={() => openDetail(p.id)}><History className="h-3.5 w-3.5" /></Button>
                      {canEdit && <>
                        <Tip label="Edit this product"><Button variant="ghost" size="icon" aria-label="Edit" data-testid={`product-edit-${p.id}`} onClick={() => startEdit(p)}><Pencil className="h-4 w-4" /></Button></Tip>
                        <Tip label="Duplicate, then change its flavor, size or prices"><Button variant="ghost" size="icon" aria-label="Duplicate" data-testid={`product-duplicate-${p.id}`} onClick={() => startDuplicate(p)}><Copy className="h-4 w-4" /></Button></Tip>
                        <Tip label="Delete this product"><Button variant="ghost" size="icon" aria-label="Delete" data-testid={`product-delete-${p.id}`} onClick={() => del(p.id)}><Trash2 className="h-4 w-4 text-destructive" /></Button></Tip>
                      </>}
                    </div>
                  </Card>
                );
              })}
            </div>
          )}

          {!groupSizes && view === "table" && (
            <Card className="overflow-x-auto" data-testid="products-table">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Size</TableHead>
                    <TableHead>UPC</TableHead>
                    <TableHead className="text-right">Cost</TableHead>
                    <TableHead className="text-right">Vend</TableHead>
                    <TableHead className="text-right">Margin</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {visibleItems.map((p) => {
                    const mi = productMargin(p, globalMargin);
                    return (
                      <TableRow key={p.id} data-testid={`product-row-${p.id}`}>
                        <TableCell className="font-medium" data-testid={`product-name-${p.id}`}>{productLabel(p)}</TableCell>
                        <TableCell><Badge variant="secondary">{p.category}</Badge></TableCell>
                        <TableCell data-testid={`product-size-${p.id}`}>{p.size || "—"}</TableCell>
                        <TableCell className="font-mono text-xs" data-testid={`product-upc-${p.id}`}>{p.upc || "—"}</TableCell>
                        <TableCell className="text-right font-mono">{p.cost != null ? `$${Number(p.cost).toFixed(2)}` : "—"}</TableCell>
                        <TableCell className="text-right font-mono">{p.vend_price != null ? `$${Number(p.vend_price).toFixed(2)}` : "—"}</TableCell>
                        <TableCell className="text-right" data-testid={`product-margin-${p.id}`}>{mi.margin != null ? <span className={mi.below ? "font-semibold text-destructive" : "font-semibold text-green-700"}>{mi.margin}%</span> : "—"}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Tip label="Price history and store prices"><Button variant="ghost" size="icon" aria-label="Price history" data-testid={`product-history-${p.id}`} onClick={() => openDetail(p.id)}><History className="h-4 w-4" /></Button></Tip>
                            {canEdit && <>
                              <Tip label="Edit this product"><Button variant="ghost" size="icon" aria-label="Edit" data-testid={`product-edit-${p.id}`} onClick={() => startEdit(p)}><Pencil className="h-4 w-4" /></Button></Tip>
                        <Tip label="Duplicate, then change its flavor, size or prices"><Button variant="ghost" size="icon" aria-label="Duplicate" data-testid={`product-duplicate-${p.id}`} onClick={() => startDuplicate(p)}><Copy className="h-4 w-4" /></Button></Tip>
                              <Tip label="Delete this product"><Button variant="ghost" size="icon" aria-label="Delete" data-testid={`product-delete-${p.id}`} onClick={() => del(p.id)}><Trash2 className="h-4 w-4 text-destructive" /></Button></Tip>
                            </>}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Card>
          )}
        </>
      )}

      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setDuplicating(false); setPendingPhoto(null); } }}>
        <DialogContent data-testid="product-dialog">
          <DialogHeader><DialogTitle>{editId ? "Edit product" : duplicating ? "Duplicate product" : "Add product"}</DialogTitle></DialogHeader>
          {duplicating && <p className="-mt-2 text-xs text-muted-foreground">A copy of {productLabel(form)}{form.size ? ` (${form.size})` : ""}. Change the flavor, size or prices, then save to add it.</p>}
          <div className="space-y-3">
            <div><Label>Name *</Label><Input data-testid="product-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Coca-Cola Classic" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Variant / flavor</Label><Input data-testid="product-variant-input" value={form.variant} onChange={(e) => setForm({ ...form, variant: e.target.value })} placeholder="Cherry" /></div>
              <div><Label>Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="product-category-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Size / package</Label>
              <Input list="size-suggestions" data-testid="product-size-input" value={form.size} onChange={(e) => setForm({ ...form, size: e.target.value })} placeholder="e.g. 12oz can, 16.9oz bottle, 20oz bottle" />
              <datalist id="size-suggestions">{SIZE_SUGGESTIONS.map((s) => <option key={s} value={s} />)}</datalist>
              <p className="mt-1 text-[11px] text-muted-foreground">Shown on the planogram so stockers know exactly which package to load.</p>
            </div>
            <div><Label>UPC</Label><Input data-testid="product-upc-input" value={form.upc} onChange={(e) => setForm({ ...form, upc: e.target.value })} placeholder="049000028900" /></div>
            <div><Label>Target cost / item ($)</Label><Input data-testid="product-target-input" type="number" step="0.01" value={form.target_cost} onChange={(e) => setForm({ ...form, target_cost: e.target.value })} placeholder="0.50" /></div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Unit cost ($)</Label><Input data-testid="product-cost-input" type="number" step="0.01" value={form.cost} onChange={(e) => setForm({ ...form, cost: e.target.value })} placeholder="0.60" /><p className="mt-0.5 text-[10px] text-muted-foreground">Optional if case price set</p></div>
              <div>
                <Label>Vend price ($)</Label>
                <Input data-testid="product-vend-input" type="number" step="0.01" value={form.vend_price} onChange={(e) => setForm({ ...form, vend_price: e.target.value })} placeholder="auto" />
                {(() => { const m = form.target_margin_pct !== "" ? Number(form.target_margin_pct) : globalMargin; const uc = effUnitCost(form); const rec = vendForMargin(uc, m); return rec > 0 ? (
                  <button type="button" className="mt-1 text-[11px] font-semibold text-primary" data-testid="product-vend-recommend" onClick={() => setForm({ ...form, vend_price: rec })}>Use ${rec.toFixed(2)} ({markupFor(uc, m)}% margin)</button>
                ) : null; })()}
              </div>
              <div><Label>Margin % override</Label><Input data-testid="product-margin-input" type="number" min="0" max="500" value={form.target_margin_pct} onChange={(e) => setForm({ ...form, target_margin_pct: e.target.value })} placeholder={`default ${markupLabel(globalMargin)}`} /></div>
            </div>
            <div className="rounded-lg border bg-muted/40 p-3">
              <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Case buying</div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Case pack (units per case)</Label><Input data-testid="product-casepack-input" type="number" value={form.case_pack} onChange={(e) => setForm({ ...form, case_pack: e.target.value })} placeholder="24" /></div>
                <div><Label>Case price ($)</Label><Input data-testid="product-caseprice-input" type="number" step="0.01" value={form.case_price} onChange={(e) => setForm({ ...form, case_price: e.target.value })} placeholder="10.80" /></div>
              </div>
              {(() => {
                const uc = effUnitCost(form);
                const tmRule = form.target_margin_pct !== "" ? Number(form.target_margin_pct) : globalMargin;
                const tm = markupFor(uc, tmRule);
                const vend = form.vend_price !== "" && form.vend_price != null ? Number(form.vend_price) : (uc > 0 ? vendForMargin(uc, tmRule) : null);
                const margin = vend && vend > 0 ? marginOnCost(uc, vend) : null;
                if (uc == null) return <p className="mt-2 text-[11px] text-muted-foreground">Enter a case price + case pack (or a unit cost) to compute cost &amp; margin.</p>;
                return (
                  <p className="mt-2 text-xs text-foreground" data-testid="product-cost-computed">
                    Unit cost <b>${uc.toFixed(3)}</b>{form.case_price !== "" && Number(form.case_pack) > 0 ? <span className="text-muted-foreground"> (${Number(form.case_price).toFixed(2)} ÷ {form.case_pack})</span> : null}
                    {margin != null && <> · margin at ${vend.toFixed(2)}: <b className={margin >= tm ? "text-green-700" : "text-destructive"}>{margin}%</b> <span className="text-muted-foreground">(target {tm}%)</span></>}
                  </p>
                );
              })()}
            </div>
            <div>
              <Label>Product image</Label>
              <div className="flex items-center gap-3 mt-1">
                {pendingPhoto || form.image_path ? <img src={pendingPhoto || imgUrl(form.image_path)} alt="product" className="h-14 w-14 rounded-lg border object-cover" data-testid="product-image-preview" /> : <div className="h-14 w-14 rounded-lg border bg-muted flex items-center justify-center text-[10px] text-muted-foreground">No image</div>}
                <Button type="button" variant="outline" size="sm" className="shrink-0" onClick={() => productPhotoInput.current.click()} disabled={photoBusy} data-testid="product-photo-upload"><Camera className="mr-1.5 h-4 w-4" />{photoBusy ? "Saving…" : "Photo"}</Button>
                <input ref={productPhotoInput} type="file" accept="image/*" className="sr-only" data-testid="product-photo-file" onChange={(e) => { pickProductPhoto(e.target.files[0]); e.target.value = ""; }} />
                <Input data-testid="product-image-url" type="url" placeholder="or paste an https://… image link" value={form.image_path?.startsWith("/api/") ? "" : form.image_path || ""} onChange={(e) => setForm({ ...form, image_path: e.target.value })} />
                {(form.image_path || pendingPhoto) && <Button type="button" variant="ghost" size="sm" data-testid="product-image-clear" onClick={() => { setForm({ ...form, image_path: "" }); setPendingPhoto(null); }}>Remove</Button>}
              </div>
            </div>
            <div>
              <Label>Store item numbers</Label>
              <p className="text-[11px] text-muted-foreground mb-1">Retailer-specific item #s (used for one-tap store lookup links).</p>
              <div className="grid grid-cols-2 gap-2">
                {ITEM_NUMBER_STORES.map((store) => (
                  <Input
                    key={store}
                    data-testid={`product-itemno-${store.replace(/[^a-z]/gi, "").toLowerCase()}`}
                    placeholder={`${store} item #`}
                    value={form.store_item_numbers?.[store] || ""}
                    onChange={(e) => setForm({ ...form, store_item_numbers: { ...form.store_item_numbers, [store]: e.target.value } })}
                  />
                ))}
              </div>
            </div>
          </div>
          <DialogFooter><Button data-testid="product-save-button" onClick={save}>Save product</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent data-testid="product-detail-dialog" className="max-w-2xl">
          <DialogHeader><DialogTitle>{productLabel(detail)} — shared price trends</DialogTitle></DialogHeader>

          {trends?.best_buy && (
            <div className="flex flex-wrap items-center gap-2 rounded-lg border border-green-300 bg-green-50 px-3 py-2 text-sm" data-testid="product-best-buy">
              <Award className="h-4 w-4 text-green-700" />
              <span className="font-semibold text-green-800">Best buy: ${trends.best_buy.unit_price}/unit</span>
              <span className="text-green-700">at {trends.best_buy.store}</span>
              {detail?.avg_unit_price != null && (
                <span className="ml-auto rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-semibold text-primary" data-testid="product-avg-price">
                  Price to beat (avg): ${detail.avg_unit_price}/unit · {detail.avg_count} price{detail.avg_count !== 1 ? "s" : ""}
                </span>
              )}
            </div>
          )}

          {trends?.points?.length ? (
            <div className="mt-1" data-testid="product-trend-chart">
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1"><TrendingDown className="h-3.5 w-3.5" />Per-unit price over time (all contributors)</div>
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={chartData} margin={{ top: 6, right: 10, left: -18, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `$${v}`} />
                  <Tooltip formatter={(v) => (v == null ? "—" : `$${v}`)} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  {trends.stores.map((s, i) => (
                    <Line key={s} type="monotone" dataKey={s} stroke={STORE_COLORS[i % STORE_COLORS.length]} strokeWidth={2} connectNulls dot={{ r: 3 }} />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>
          ) : null}

          <div className="mt-2 text-xs font-semibold text-muted-foreground">Contributions {detail?.avg_unit_price != null && `· avg $${detail.avg_unit_price}/unit`}</div>
          {detail?.price_history?.length ? (
            <div className="space-y-2 max-h-[36vh] overflow-y-auto">
              {detail.price_history.filter((h) => isAdmin || !h.hidden).map((h) => {
                const ds = !h.hidden ? dealSticker(h.unit_price, detail?.avg_unit_price) : null;
                return (
                <div key={h.id} className={`flex items-center justify-between rounded-lg border p-3 text-sm ${h.hidden ? "opacity-50 bg-muted" : ""} ${h.flagged && !h.hidden ? "border-amber-300 bg-amber-50" : ""}`} data-testid={`capture-row-${h.id}`}>
                  <div className="min-w-0">
                    <div className="font-medium flex flex-wrap items-center gap-1.5">
                      {h.store_name}
                      {h.on_sale && <span className="inline-flex items-center gap-0.5 rounded-full bg-rose-600 px-1.5 text-[10px] font-bold text-white" data-testid={`capture-onsale-${h.id}`}><Tag className="h-2.5 w-2.5" />ON SALE</span>}
                      {ds && <span className="rounded-full px-1.5 text-[10px] font-bold" style={{ background: ds.bg, color: ds.text, border: `1px solid ${ds.border}` }} data-testid={`capture-deal-${h.id}`}>{ds.tone === "good" ? "\u25BC" : "\u25B2"} {ds.short}</span>}
                      {h.flagged && !h.hidden && <span className="inline-flex items-center gap-0.5 rounded-full bg-amber-500 px-1.5 text-[10px] font-bold text-white" data-testid={`capture-outlier-${h.id}`}><AlertTriangle className="h-2.5 w-2.5" />REVIEW</span>}
                      {h.hidden && !h.pending_review && <span className="rounded-full bg-slate-400 px-1.5 text-[10px] font-bold text-white">HIDDEN</span>}
                      {h.pending_review && <span className="rounded-full bg-amber-600 px-1.5 text-[10px] font-bold text-white" data-testid={`capture-pending-${h.id}`}>PENDING</span>}
                    </div>
                    <div className="text-xs text-muted-foreground flex items-center gap-1"><User className="h-3 w-3" />{h.user_name || "Unknown"} · {new Date(h.captured_date).toLocaleDateString()}</div>
                    {h.flag_reason && <div className="text-[11px] text-amber-700">{h.flag_reason}</div>}
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="text-right"><div className="font-mono font-semibold">${h.captured_price}</div><div className="text-xs text-muted-foreground">${h.unit_price}/unit</div></div>
                    {isAdmin && h.pending_review && (
                      <>
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-green-700" title="Approve (include in average)" data-testid={`capture-approve-${h.id}`} onClick={() => reviewCapture(h.id, "approve")}><CheckCircle2 className="h-4 w-4" /></Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-red-700" title="Reject (keep hidden)" data-testid={`capture-reject-${h.id}`} onClick={() => reviewCapture(h.id, "reject")}><XCircle className="h-4 w-4" /></Button>
                      </>
                    )}
                    {isAdmin && !h.pending_review && (
                      <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`capture-hide-${h.id}`} title={h.hidden ? "Unhide" : "Hide from shared catalog"} onClick={() => flagCapture(h.id, { hidden: !h.hidden, flagged: h.hidden ? h.flagged : false })}>
                        {h.hidden ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4 text-destructive" />}
                      </Button>
                    )}
                  </div>
                </div>
                );
              })}
            </div>
          ) : <p className="text-sm text-muted-foreground">No captures recorded yet.</p>}
        </DialogContent>
      </Dialog>

      <Dialog open={logOpen} onOpenChange={setLogOpen}>
        <DialogContent data-testid="review-log-dialog" className="max-w-2xl">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><ScrollText className="h-5 w-5" />Outlier review history</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground -mt-1">Audit trail of every approved or rejected price outlier across the shared catalog.</p>
          {log === null ? (
            <div className="space-y-2">{[0, 1, 2].map((i) => <div key={i} className="h-16 animate-pulse rounded-lg bg-muted" />)}</div>
          ) : log.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground" data-testid="review-log-empty">No outliers have been reviewed yet.</p>
          ) : (
            <div className="space-y-2 max-h-[60vh] overflow-y-auto">
              {log.map((r) => {
                const approved = r.review_status === "approved";
                return (
                  <div key={r.id} className={`flex items-start justify-between gap-3 rounded-lg border p-3 text-sm ${approved ? "border-green-200 bg-green-50/50" : "border-red-200 bg-red-50/50"}`} data-testid={`review-log-row-${r.id}`}>
                    <div className="min-w-0">
                      <div className="flex items-center gap-1.5 font-medium">
                        {approved
                          ? <span className="inline-flex items-center gap-0.5 rounded-full bg-green-600 px-1.5 text-[10px] font-bold text-white"><CheckCircle2 className="h-2.5 w-2.5" />APPROVED</span>
                          : <span className="inline-flex items-center gap-0.5 rounded-full bg-red-600 px-1.5 text-[10px] font-bold text-white"><XCircle className="h-2.5 w-2.5" />REJECTED</span>}
                        <span className="truncate">{r.product_name}</span>
                      </div>
                      <div className="mt-0.5 text-xs text-muted-foreground">
                        {r.store_name} · submitted by {r.contributor || "Unknown"}
                      </div>
                      <div className="mt-0.5 flex flex-wrap items-center gap-1 text-[11px] text-muted-foreground">
                        {r.auto ? <Bot className="h-3 w-3" /> : <User className="h-3 w-3" />}
                        <span className="font-medium text-foreground">{r.reviewed_by || "—"}</span>
                        <span>· {r.reviewed_at ? new Date(r.reviewed_at).toLocaleString() : ""}</span>
                      </div>
                      {r.flag_reason && <div className="mt-0.5 text-[11px] text-muted-foreground italic">{r.flag_reason}</div>}
                    </div>
                    <div className="shrink-0 text-right"><div className="font-mono font-semibold">${r.captured_price}</div><div className="text-xs text-muted-foreground">${r.unit_price}/unit</div></div>
                  </div>
                );
              })}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!mergeSrc} onOpenChange={(o) => !o && setMergeSrc(null)}>
        <DialogContent data-testid="merge-group-dialog">
          <DialogHeader><DialogTitle>Merge “{mergeSrc?.name}” into another product</DialogTitle></DialogHeader>
          <p className="-mt-1 text-xs text-muted-foreground">
            All {mergeSrc?.items?.length} size{mergeSrc?.items?.length !== 1 ? "s" : ""} of “{mergeSrc?.name}” will be renamed to the product you pick, so they appear as one grouped entry. Prices and sizes are kept.
          </p>
          <div>
            <Label>Merge into</Label>
            <Select value={mergeTarget} onValueChange={setMergeTarget}>
              <SelectTrigger data-testid="merge-target-select"><SelectValue placeholder="Choose a product…" /></SelectTrigger>
              <SelectContent>
                {sizeGroups.filter((g) => g.name !== mergeSrc?.name).map((g) => (
                  <SelectItem key={g.name} value={g.name}>{g.name} ({g.items.length} size{g.items.length !== 1 ? "s" : ""})</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMergeSrc(null)} data-testid="merge-cancel">Cancel</Button>
            <Button onClick={doMerge} disabled={!mergeTarget || mergeBusy} data-testid="merge-confirm"><GitMerge className="mr-1 h-4 w-4" />{mergeBusy ? "Merging…" : "Merge"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      <Dialog open={repriceOpen} onOpenChange={setRepriceOpen}>
        <DialogContent data-testid="bulk-reprice-dialog">
          <DialogHeader><DialogTitle>Bulk price tune</DialogTitle></DialogHeader>
          <p className="-mt-1 text-xs text-muted-foreground">
            Price every product with a known cost by your markup rule (or one flat markup), rounded up to the next $0.25. Products already at the right price are skipped, and the rule never lowers a price you set higher.
          </p>
          <div className="rounded-lg border p-3">
            <div className="flex items-end gap-3">
              <div className="w-32">
                <Label className="text-xs">Target margin %</Label>
                <Input data-testid="reprice-margin-input" type="number" min="0" max="500" value={repriceMargin === "rule" ? "" : repriceMargin} placeholder="rule"
                  onChange={(e) => setRepriceMargin(e.target.value)} />
              </div>
              <div className="flex flex-wrap gap-1.5 pb-0.5">
                <button type="button" data-testid="reprice-preset-rule" onClick={() => setRepriceMargin("rule")}
                  className={`rounded-full border px-2.5 py-1 text-xs font-medium transition-colors ${repriceMargin === "rule" ? "border-primary bg-primary/10 text-primary" : "text-muted-foreground hover:bg-muted"}`}>
                  My rule ({markupLabel(globalMargin)})
                </button>
                {[50, 65, 75, 100].map((m) => (
                  <button key={m} type="button" data-testid={`reprice-preset-${m}`}
                    onClick={() => setRepriceMargin(m)}
                    className={`rounded-full border px-2.5 py-1 text-xs font-medium transition-colors ${Number(repriceMargin) === m ? "border-primary bg-primary/10 text-primary" : "text-muted-foreground hover:bg-muted"}`}>
                    {m}%
                  </button>
                ))}
              </div>
            </div>
            {repriceMargin !== "rule" && <label className="mt-3 flex cursor-pointer items-center gap-2 text-xs text-muted-foreground">
              <Checkbox checked={saveDefaultMargin} onCheckedChange={(v) => setSaveDefaultMargin(!!v)} data-testid="reprice-save-default" />
              Also save {Number(repriceMargin) || 0}% as my one default markup (turns off the tiers)
            </label>}
          </div>
          {repriceCandidates.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground" data-testid="bulk-reprice-empty">Everything is already priced to {repriceMargin === "rule" ? markupLabel(globalMargin) : `${Number(repriceMargin)}%`} markup. Nothing to change.</p>
          ) : (
            <div className="space-y-1.5 max-h-[42vh] overflow-y-auto pr-1">
              {repriceCandidates.map((x) => (
                <div key={x.p.id} className="flex items-center justify-between gap-3 rounded-lg border p-2.5 text-sm" data-testid={`reprice-row-${x.p.id}`}>
                  <div className="min-w-0">
                    <div className="truncate font-medium">{productLabel(x.p)}</div>
                    <div className="text-xs text-muted-foreground">{x.uc != null ? `cost $${x.uc.toFixed(2)}` : "cost —"}</div>
                  </div>
                  <div className="flex shrink-0 items-center gap-2 font-mono text-sm">
                    <span className="text-muted-foreground">{x.current != null ? `$${x.current.toFixed(2)}` : "—"}</span>
                    <TrendingDown className="h-3.5 w-3.5 text-muted-foreground rotate-180" />
                    <span className="font-semibold text-green-700" data-testid={`reprice-new-${x.p.id}`}>${x.rec.toFixed(2)}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setRepriceOpen(false)} data-testid="bulk-reprice-cancel">Cancel</Button>
            <Button onClick={applyBulkReprice} disabled={repriceBusy || repriceCandidates.length === 0} data-testid="bulk-reprice-apply">
              <DollarSign className="mr-1 h-4 w-4" />{repriceBusy ? "Applying…" : `Apply to ${repriceCandidates.length}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    </TooltipProvider>
  );
}
